/**
 * Notifications push côté PWA : autorisation + abonnement Web Push.
 * Fonctionne sur Android et sur iPhone 16.4+ UNE FOIS la PWA installée sur
 * l'écran d'accueil (sinon iOS bloque l'abonnement).
 */
import { jetonValide } from './auth.ts';

const BASE = import.meta.env.VITE_API_URL ?? '';

export function notificationsSupportees(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function etatAutorisation(): NotificationPermission | 'non-supporte' {
  if (!notificationsSupportees()) return 'non-supporte';
  return Notification.permission;
}

/** Endpoint de l'abonnement push de CET appareil (pour cibler un test « sur cet
 *  appareil »). null si non abonné / non supporté. */
export async function endpointPushActuel(): Promise<string | null> {
  if (!notificationsSupportees()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
}

/** Convertit une clé VAPID base64url en Uint8Array (format applicationServerKey).
 *  Décodage MANUEL (sans `atob`) : Safari/iOS lève « The string did not match the
 *  expected pattern » sur atob de certaines chaînes pourtant valides. On décode
 *  directement l'alphabet base64url, sans padding requis. Adossé à un ArrayBuffer
 *  concret (requis par BufferSource en TS 6). */
function vapidEnTableau(base64url: string): Uint8Array<ArrayBuffer> {
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const clean = base64url.replace(/[^A-Za-z0-9\-_]/g, '');
  const idx = (ch: string | undefined): number => (ch ? ALPHABET.indexOf(ch) : -1);
  const octets: number[] = [];
  for (let i = 0; i < clean.length; i += 4) {
    const reste = clean.length - i;
    // Un groupe base64 valide a 2, 3 ou 4 caractères (jamais 1 isolé).
    if (reste < 2) break;
    const c0 = idx(clean[i]);
    const c1 = idx(clean[i + 1]);
    octets.push((c0 << 2) | (c1 >> 4));
    if (reste >= 3) {
      const c2 = idx(clean[i + 2]);
      octets.push(((c1 & 15) << 4) | (c2 >> 2));
      if (reste >= 4) {
        octets.push(((c2 & 3) << 6) | idx(clean[i + 3]));
      }
    }
  }
  return new Uint8Array(octets);
}

/**
 * Demande l'autorisation, s'abonne au push, envoie l'abonnement à l'API.
 * Renvoie true si l'abonnement est actif.
 */
/** Tente l'abonnement avec la clé en Uint8Array, puis en repli avec la STRING
 *  base64url brute (certaines versions de Safari/iOS rejettent l'un ou l'autre). */
async function souscrire(
  reg: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscription> {
  try {
    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidEnTableau(publicKey),
    });
  } catch (e1) {
    // Repli : applicationServerKey accepte aussi une string base64url sur certains WebKit.
    try {
      return await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: publicKey as unknown as BufferSource,
      });
    } catch (e2) {
      const n1 = e1 instanceof Error ? e1.name || e1.message : 'KO';
      const n2 = e2 instanceof Error ? e2.name || e2.message : 'KO';
      throw new Error(`subscribe ${n1}/${n2}`);
    }
  }
}

export async function activerNotifications(): Promise<boolean> {
  if (!notificationsSupportees()) return false;
  // Étape courante : tracée pour que le message d'erreur nomme la ligne exacte
  // qui échoue (impossible d'inspecter la console iOS à distance).
  let etape = 'permission';
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    etape = 'sw-ready';
    const reg = await navigator.serviceWorker.ready;

    etape = 'cle-vapid';
    const res = await fetch(`${BASE}/push/public-key`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { publicKey } = (await res.json()) as { publicKey: string | null };
    if (!publicKey) throw new Error('publicKey vide');

    etape = 'get-subscription';
    // Réutilise l'abonnement existant si présent (rappeler subscribe() avec une
    // souscription déjà posée lève sur iOS) ; sinon on en crée un.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      etape = 'subscribe';
      sub = await souscrire(reg, publicKey);
    }

    etape = 'enregistrement';
    // Bearer si connecté : en mode auth obligatoire, /push/subscribe l'exige (la clé
    // VAPID publique reste ouverte). Sans connexion, l'en-tête est simplement absent.
    const jeton = await jetonValide();
    const envoi = await fetch(`${BASE}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
      },
      body: JSON.stringify(sub),
    });
    if (!envoi.ok) throw new Error(`HTTP ${envoi.status}`);
    return true;
  } catch (e) {
    // Diagnostic complet en un seul message (pas de console iOS accessible) :
    // étape + type d'exception + message + base API vue à l'exécution.
    const nom = e instanceof Error ? e.name : typeof e;
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`[${etape}] ${nom}: ${msg} | base=${BASE || '(vide)'}`);
  }
}
